import React from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { truncate } from 'Components/utils/string/string.util';
import { usePageContext } from 'Context/PageContext';
import { t } from 'Src/i18n';
import { STATIC_PAGE_TYPES } from 'Tracking/constants';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Badge } from 'Components/frameworks/badge/Badge';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Image } from 'Components/frameworks/image/Image';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
type Props = {
  className?: string;
  firstItemDisplay?: boolean;
  hasCounters?: boolean;
  label?: string;
  onClick: () => void;
  thematic: ActivitiesThematicEntity;
  url?: string;
};
export const ActivitiesThematicCard = ({
  className,
  firstItemDisplay = false,
  hasCounters = false,
  label,
  onClick,
  thematic,
  url,
  ...rest
}: Props) => {
  const { pageType } = usePageContext();
  const { isDesktop } = useResponsive();
  const isPle = pageType === STATIC_PAGE_TYPES.PLE;
  const onlineBookableActivitiesCount = thematic?.onlineBookableCount || 0;
  // in PLE we only show the online bookable activities count
  const totalActivities = isPle
    ? onlineBookableActivitiesCount
    : (thematic?.activitiesCounters?.included || 0) + (thematic?.activitiesCounters?.onDemand || 0);
  const firstItemTextLength = isDesktop ? 40 : 25;
  const firstItemTitle = truncate(thematic.name, isDesktop ? 35 : 25);
  const defaultItemTextLength = isDesktop ? 90 : 42;
  const defaultItemTitle = thematic.name;
  return (
    <DynamicLink
      {...rest}
      className={classnames(
        'group relative isolate flex h-310 items-end justify-between overflow-hidden rounded-12 p-20 text-white',
        className,
      )}
      data-testid="thematics"
      onClick={onClick}
      shallow
      url={url}
    >
      <div className="absolute inset-0 -z-1 flex transform items-center transition-all duration-250 ease-in group-hover:scale-105">
        <Image className="flex h-full w-full" imageUrl={thematic.images?.[0]} />
        <div className="absolute bottom-0 h-1/2 w-full bg-gradient-to-t from-black/80" />
      </div>

      {hasCounters && (
        <div className="absolute top-0 mt-10 flex flex-row items-center">
          {thematic?.activitiesCounters?.onDemand > 0 && (
            <Badge
              className="size-30 border-orange bg-orange text-white"
              iconName={ICONS.ON_DEMAND}
              iconSize="0.875rem"
              variant="pill"
            />
          )}
          {thematic?.activitiesCounters?.included > 0 && (
            <Badge
              className="size-30 border-green bg-green text-white"
              iconName={ICONS.ALL_INCLUSIVE}
              iconSize="0.875rem"
              variant="pill"
            />
          )}
          <span className="ms-10 font-brand">
            {t(
              totalActivities > 1
                ? 'common.activities.counters._n'
                : 'common.activities.counters._1',
              {
                count: totalActivities,
              },
            )}
          </span>
        </div>
      )}

      <div>
        <Title className="mb-5 text-white" theme="title-brand-n0">
          {firstItemDisplay ? firstItemTitle : defaultItemTitle}
        </Title>
        <Text
          className="text-white"
          text={truncate(
            thematic.description || '',
            firstItemDisplay ? firstItemTextLength : defaultItemTextLength,
          )}
          theme="text-sans-n3"
        />
      </div>
      <Icon className="shrink-0" hasRtl name={ICONS.ARROW_RIGHT} width="1rem" />
    </DynamicLink>
  );
};
